import React, {
  useState,
  useMemo,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import styles from "app/style.module.scss";
import {
  Box,
  Link,
  Button,
  FormField,
  SpaceBetween,
  Spinner,
} from "@amzn/awsui-components-react/polaris";
import {
  ColumnLayout,
  Container,
  Form,
  Header,
  Input,
} from "@amzn/awsui-components-react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "features/notifications";
import {
  ControlledInput,
  ControlledSelect,
  ControlledDatePicker,
  ControlledMultiselect,
  ControlledTextarea,
} from "@amzn/react-hook-form-polaris";
import {
  AuditFormProps,
  AuditFormRef,
  RegisterDocumentRequest,
  UploadUrlRequest,
  UploadUrlResponse,
} from "components/create-audit-form/definitions";
import {
  useCreateAuditMutation,
  useRegisterDocumentMutation,
  useUploadUrlMutation,
} from "components/create-audit-form/endpoints";
import { get, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAppSelector } from "app/store";
import { getAuditErrorMessage, findTemplateValueByLabel } from "./utils";
import { useTeamNameSelector } from "../create-audit-form/hooks";
import AuditName from "components/audit-name";
import DynamicFormField from "components/dynamic-form-field";
import { CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME } from "models/generateCustomAttributesSchema";
import { z } from "zod";
import { generateDynamicAttributesDataMap } from "utils/dynamicAttributesUtils";
import { getAuditName } from "components/audit-name/AuditName";
import { AuditNameProps } from "../audit-name/definitions";
import UploadFile from "components/upload-file";
import {
  getFileExtension,
  getFileNameWithoutExtension,
  getFileSize,
  toUnixEpochTime,
} from "utils/date-format";
import { useGetContactsListQuery } from "components/contacts-table/endpoints";
import { useTeamSpecificAuditTemplates } from "../../pages/audits/table-config";
import { useAuditSchemaAwareLabels } from "hooks/useSchemaAwareLabels";
import { determineJurisdictionLevel } from "utils/jurisdictionUtils";
import {
  getFilteredOptions,
  getEntityCodeFromLongName,
} from "features/ui-content/utils";
import { validateAndResetGovEntity } from "utils/gaiaobject-utils";
import {
  useGetScanResultQuery,
  useGetScanjobQuery,
} from "pages/draft-overview/endpoints";
import { buildDraftAuditFormResetData } from "components/draft-wizard/utils";
import { transformScanResultToAuditFormDataWithMapping } from "components/draft-wizard/field-mappings";
import { GaiaConstants } from "utils";
import { useLocalStorage } from "hooks/useLocalStorage";

const CreateAuditForm = forwardRef<AuditFormRef, AuditFormProps>(
  (
    {
      wizardMode = false,
      mode = "create",
      shortId,
      defaultOwner,
      onFormSubmit,
    },
    ref
  ) => {
    const createLabel = useAuditSchemaAwareLabels();
    const auditCustomAttributes = useAppSelector(
      (state) =>
        state.teamSettings.selectedTeamMainMetaData.teamCustomAttributesSchema
          ?.audit
    );
    const createAuditSchema = useAppSelector(
      (state) => state.teamSettings.auditSchemas.auditFormSchema
    );
    const auditSchema = useAppSelector(
      (state) => state.teamSettings.auditSchemas.auditSchema
    );

    const { data: scanResultData } = useGetScanResultQuery(
      { shortId: shortId! },
      { refetchOnMountOrArgChange: true, skip: mode !== "draft" || !shortId }
    );

    const { data: scanJobData } = useGetScanjobQuery(
      { shortId: shortId! },
      { refetchOnMountOrArgChange: true, skip: mode !== "draft" || !shortId }
    );

    const localStorageKey = `draft-wizard-step1-${shortId || "default"}`;
    const [localFormData, setLocalFormData] = useLocalStorage<any>(
      localStorageKey,
      null
    );

    const { handleSubmit, control, formState, watch, setValue, reset } =
      useForm({
        mode: "onBlur",
        resolver: zodResolver(createAuditSchema),
      });
    const [disableCreate, setDisableCreate] = React.useState(false);
    const [files, setFiles] = useState<File[]>([]);
    const { teamName } = useTeamNameSelector();
    const getTemplatesByTeamName = useTeamSpecificAuditTemplates();
    const { data: contactsList } = useGetContactsListQuery(
      { teamName: teamName },
      {
        refetchOnFocus: true,
        skip: !teamName || teamName === GaiaConstants.PLACE_HOLDER,
      }
    );

    const onValid = (data: any) => {
      setDisableCreate(true);
      if (wizardMode && onFormSubmit) {
        onFormSubmit(data);
      } else {
        files[0] ? onCreateAuditWithDoc(data) : onCreateAudit(data);
      }
    };

    const onInvalid = () => {};

    useImperativeHandle(ref, () => ({
      getFormData: () => {
        const formData = watch();
        if (wizardMode && shortId) {
          setLocalFormData(formData);
        }
        if (wizardMode && formData.templateId) {
          return {
            ...formData,
          };
        }
        return formData;
      },
      submitForm: () => {
        handleSubmit(onValid, onInvalid)();
      },
    }));

    const country = watch("country");
    const governmentEntity = watch("governmentEntity");
    const localJurisdiction = watch("localJurisdiction");
    const entity = watch("entity");
    const taxType = watch("taxType");
    const auditStartDateString = watch("auditStartDateString");
    const auditEndDateString = watch("auditEndDateString");
    const auditNameSuffix = watch("auditNameSuffix");
    const data: any = useAppSelector((state) => state.uiContent);
    const currentUser = useAppSelector((state) => state.auth.user);
    const defaultCountry =
      data?.defaultCountry ?? data?.countryOptions?.[0].value ?? "";
    const templateId = watch("templateId");
    const entityCode = watch("entityCode");
    // The Government entity field must be reactive to the user's Country selection.
    const filteredGovEntityOptions = useMemo(() => {
      return getFilteredOptions(
        data?.governmentEntityOptions || [],
        data?.countryOptions || [],
        "country",
        country
      );
    }, [data?.governmentEntityOptions, data?.countryOptions, country]);

    useEffect(() => {
      if (mode === "draft" && scanResultData) {
        const scanFormData = transformScanResultToAuditFormDataWithMapping(
          scanResultData.scanResult,
          teamName,
          auditCustomAttributes,
          data,
          scanJobData?.scanJob?.createdBy
        );

        if (wizardMode && localFormData && shortId) {
          const mergedData = {
            ...buildDraftAuditFormResetData(scanFormData),
            ...localFormData,
          };
          if (
            !localFormData.governmentEntity &&
            scanFormData.governmentEntity
          ) {
            mergedData.governmentEntity = scanFormData.governmentEntity;
          }
          if (mergedData.entity && data?.entityOptions) {
            const entityCodeFromName = getEntityCodeFromLongName(
              mergedData.entity,
              data
            );
            if (entityCodeFromName) {
              mergedData.entityCode = entityCodeFromName;
            }
          }
          reset(mergedData);
        } else {
          const resetData = buildDraftAuditFormResetData(scanFormData);
          if (resetData.entity && data?.entityOptions) {
            const entityCodeFromName = getEntityCodeFromLongName(
              resetData.entity,
              data
            );
            if (entityCodeFromName) {
              resetData.entityCode = entityCodeFromName;
            }
          }
          reset(resetData);
        }
      } else if (wizardMode && localFormData && shortId && !scanResultData) {
        reset(localFormData);
      }
    }, [
      mode,
      scanResultData,
      scanJobData,
      reset,
      auditCustomAttributes,
      data,
      teamName,
      wizardMode,
      shortId,
      localFormData,
    ]);

    useEffect(() => {
      validateAndResetGovEntity(
        country,
        governmentEntity,
        filteredGovEntityOptions,
        false, // isEditModeInit - always false for create form
        setValue,
        "" // no prefix for audit forms
      );
    }, [country, filteredGovEntityOptions, governmentEntity, setValue]);

    useEffect(() => {
      if (
        data?.ownerOptions?.length > 0 &&
        (mode === "create" || mode === "draft")
      ) {
        const currentOwnerValue = watch("owner");
        if (!currentOwnerValue) {
          const owner =
            mode === "draft" && scanJobData?.scanJob?.createdBy
              ? scanJobData.scanJob.createdBy
              : defaultOwner || currentUser?.alias;

          if (owner) {
            const ownerOption = data.ownerOptions.find(
              (option: { value: string }) => option.value === owner
            );

            if (ownerOption) {
              setValue("owner", ownerOption.value);
            }
          }
        }
      }
    }, [
      data?.ownerOptions,
      currentUser?.alias,
      mode,
      setValue,
      defaultOwner,
      scanJobData?.scanJob?.createdBy,
      watch,
    ]);
    useEffect(() => {
      if (
        mode === "draft" &&
        scanResultData?.scanTaskResult?.template &&
        wizardMode
      ) {
        const currentTemplateValue = watch("templateId");
        if (currentTemplateValue === undefined && !localFormData?.templateId) {
          const templateFromApi = scanResultData.scanTaskResult.template;
          const apiTemplateValue = findTemplateValueByLabel(
            templateFromApi,
            getTemplatesByTeamName,
            teamName
          );
          if (apiTemplateValue) {
            setValue("templateId", apiTemplateValue);
          }
        }
      }
    }, [
      mode,
      wizardMode,
      scanResultData,
      getTemplatesByTeamName,
      teamName,
      localFormData,
    ]);

    const { addNotification } = useNotifications();
    const navigate = useNavigate();
    const [createAudit] = useCreateAuditMutation();
    const [uploadUrlCall] = useUploadUrlMutation();
    const [registerDocumentCall] = useRegisterDocumentMutation();

    const processAuditData = (audit: z.infer<typeof auditSchema>) => {
      const auditNameProps: AuditNameProps = {
        country: audit.country,
        governmentEntity: audit.governmentEntity,
        localJurisdiction: audit.localJurisdiction,
        entity: audit.entity,
        taxType: audit.taxType,
        auditStartDateString: audit.auditStartDateString,
        auditEndDateString: audit.auditEndDateString,
        auditNameSuffix: audit.auditNameSuffix,
        entityOptions: data.entityOptions,
      };
      audit.auditName = getAuditName(auditNameProps);
      audit.teamName = teamName;
      audit.jurisdictionLevel = determineJurisdictionLevel(
        audit.governmentEntity,
        audit.localJurisdiction
      );
      audit.auditStartDate = toUnixEpochTime(audit.auditStartDateString);
      audit.auditEndDate = toUnixEpochTime(audit.auditEndDateString);
      audit.auditInitiationDate = toUnixEpochTime(
        audit.auditInitiationDateString
      );
      audit.auditCloseDate = toUnixEpochTime(audit.auditCloseDateString);
      audit.statuteOfLimitations = toUnixEpochTime(
        audit.statuteOfLimitationsString
      );
      audit.statusActionOwner = "Amazon"; // hardcoded to "Amazon" for now until the UI design is done
      audit.templateId = templateId;
      audit.entityCode = entityCode;

      // tranform custom attributes into data map so that it can be sent to backend
      if (auditCustomAttributes) {
        audit.customAttributesDataMap = generateDynamicAttributesDataMap(
          auditCustomAttributes,
          audit[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME]
        );
      }
      return audit;
    };

    const onCreateAudit = async (audit: z.infer<typeof auditSchema>) => {
      try {
        await createAudit(processAuditData(audit)).unwrap();
        addNotification({
          type: "success",
          header: "Audit created successfully",
          dismissible: true,
          timeout: 10000,
        });
        navigate("/audits");
      } catch (e: any) {
        const errorMessage = getAuditErrorMessage(
          e,
          "Failed to create the audit"
        );

        addNotification({
          type: "error",
          header: errorMessage,
          dismissible: true,
          timeout: 10000,
        });
        setDisableCreate(false);
      }
    };

    const onCancelCreate = async () => {
      navigate("/audits");
    };

    const onCreateAuditWithDoc = async (audit: z.infer<typeof auditSchema>) => {
      let auditResponse: any = null;
      try {
        auditResponse = await createAudit(processAuditData(audit)).unwrap();
      } catch (e: any) {
        const errorMessage = getAuditErrorMessage(
          e,
          "Failed to create the audit"
        );

        addNotification({
          type: "error",
          header: errorMessage,
          dismissible: true,
          timeout: 10000,
        });
        setDisableCreate(false);
        return;
      }

      try {
        let request: UploadUrlRequest = {
          prefix: `${GaiaConstants.AUDIT_FILES_PREFIX}/${auditResponse.auditId}`,
          fileName: files[0].name,
          teamName: teamName,
          securityLevel: "Normal",
        };
        const response: UploadUrlResponse = await uploadUrlCall(
          request
        ).unwrap();

        await fetch(response.url, {
          method: "PUT",
          body: files[0],
          headers: {
            "x-amz-expected-bucket-owner": response.bucketOwner,
          },
        });

        const s3Prefix = `${GaiaConstants.AUDIT_FILES_PREFIX}/${auditResponse.auditId}/${response.uniqueKey}/${files[0].name}`;
        const registerDocumentRequest: RegisterDocumentRequest = {
          parentId: auditResponse.auditId,
          teamName: teamName,
          fileName: getFileNameWithoutExtension(files[0].name),
          documentType: "Audit notices",
          securityLevel: "Normal",
          s3Prefix: s3Prefix,
          extentionType: getFileExtension(files[0].name),
          auditId: auditResponse.auditId,
          size: getFileSize(files[0].size),
        };
        await registerDocumentCall(registerDocumentRequest).unwrap();

        addNotification({
          type: "success",
          header: "Audit with document created successfully",
          dismissible: true,
          timeout: 10000,
        });
        navigate("/audits");
      } catch (e: any) {
        addNotification({
          type: "warning",
          header: "Audit created successfully, but file upload failed",
          content:
            "The audit was created, but the document could not be uploaded. You can upload the document later from the audit details page.",
          dismissible: true,
          timeout: 15000,
        });
        navigate("/audits");
      }
    };

    return (
      <div className={styles.createAuditForm} data-testid="create-audit-form">
        <form onSubmit={handleSubmit(onValid, onInvalid)}>
          <Form
            actions={
              wizardMode ? undefined : (
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    formAction="none"
                    onClick={onCancelCreate}
                    disabled={disableCreate}
                    variant="link"
                  >
                    Cancel
                  </Button>
                  <Button variant="primary" disabled={disableCreate}>
                    {disableCreate ? <Spinner /> : null} Create
                  </Button>
                </SpaceBetween>
              )
            }
          >
            <SpaceBetween size="l">
              <Container
                header={<Header variant="h2">Audit information</Header>}
              >
                <br />
                <ColumnLayout columns={2}>
                  <AuditName
                    country={country}
                    governmentEntity={governmentEntity}
                    localJurisdiction={localJurisdiction}
                    entity={entity}
                    taxType={taxType}
                    auditStartDateString={auditStartDateString}
                    auditEndDateString={auditEndDateString}
                    auditNameSuffix={auditNameSuffix}
                    entityOptions={data.entityOptions}
                  />
                  <FormField
                    label={createLabel("Name suffix", "auditNameSuffix")}
                    description="Additional information appended to audit name"
                    errorText={get(
                      formState?.errors,
                      `auditNameSuffix.message`
                    )}
                  >
                    <ControlledInput name="auditNameSuffix" control={control} />
                  </FormField>
                </ColumnLayout>

                <SpaceBetween size="l">
                  <Header variant="h3">Jurisdiction</Header>
                  <ColumnLayout columns={2}>
                    <FormField
                      label={createLabel("Country", "country")}
                      errorText={get(formState?.errors, `country.message`)}
                      description="Name of country involved in this audit (ie: USA)"
                    >
                      <ControlledSelect
                        name="country"
                        options={data.countryOptions}
                        control={control}
                        filteringType="auto"
                        defaultValue={defaultCountry}
                      />
                    </FormField>
                    <FormField
                      label={createLabel("State/Province", "governmentEntity")}
                      errorText={get(
                        formState?.errors,
                        `governmentEntity.message`
                      )}
                      description="Name of state / province involved in this audit. Leave blank for country-level audits."
                    >
                      <ControlledSelect
                        name="governmentEntity"
                        options={[
                          { label: "\u00A0", value: "" },
                          ...filteredGovEntityOptions,
                        ]}
                        control={control}
                        filteringType="auto"
                      />
                    </FormField>
                    <FormField
                      label={createLabel("Entity", "entity")}
                      errorText={get(formState?.errors, `entity.message`)}
                    >
                      <ControlledSelect
                        name="entity"
                        options={data.entityOptions}
                        control={control}
                        filteringType="auto"
                        onChange={(detail) => {
                          const selectedOption = detail.detail.selectedOption;
                          if (selectedOption) {
                            setValue("entity", selectedOption.value);
                            setValue(
                              "entityCode",
                              selectedOption.description || ""
                            );
                          }
                        }}
                      />
                    </FormField>
                    <FormField
                      label={createLabel("Entity code", "entityCode")}
                      description="Company code for the selected entity"
                    >
                      <Input value={entityCode} readOnly={true} />
                    </FormField>
                    <FormField
                      label={createLabel(
                        "Local jurisdiction",
                        "localJurisdiction"
                      )}
                      description="Name of local-level jurisdiction (ie: Seattle)"
                      errorText={get(
                        formState?.errors,
                        `localJurisdiction.message`
                      )}
                    >
                      <ControlledInput
                        name="localJurisdiction"
                        control={control}
                      />
                    </FormField>
                    <FormField
                      label={createLabel("Tax authority", "taxAuthority")}
                      description="Name of tax authority (ie: Internal Revenue Service)"
                      errorText={get(formState?.errors, `taxAuthority.message`)}
                    >
                      <ControlledInput name="taxAuthority" control={control} />
                    </FormField>
                  </ColumnLayout>

                  <Header variant="h3">Scope</Header>
                  <ColumnLayout columns={2}>
                    <FormField
                      label={createLabel("Tax type", "taxType")}
                      errorText={get(formState?.errors, `taxType.message`)}
                    >
                      <ControlledMultiselect
                        name="taxType"
                        control={control}
                        options={data.taxTypeOptions}
                        filteringType="auto"
                      />
                    </FormField>
                    <FormField
                      label={createLabel("Tax number", "taxNumber")}
                      errorText={get(formState?.errors, `taxNumber.message`)}
                    >
                      <ControlledInput name="taxNumber" control={control} />
                    </FormField>
                    <FormField
                      label={createLabel("Audit number", "auditNumber")}
                      errorText={get(formState?.errors, `auditNumber.message`)}
                    >
                      <ControlledInput name="auditNumber" control={control} />
                    </FormField>
                  </ColumnLayout>
                  <ColumnLayout columns={2}>
                    <FormField
                      label={createLabel(
                        "Audit period start date",
                        "auditStartDateString"
                      )}
                      errorText={get(
                        formState?.errors,
                        `auditStartDateString.message`
                      )}
                    >
                      <ControlledDatePicker
                        name="auditStartDateString"
                        control={control}
                        placeholder="YYYY/MM/DD"
                        className={styles.dateCustomPicker}
                      />
                    </FormField>
                    <FormField
                      label={createLabel(
                        "Audit period end date",
                        "auditEndDateString"
                      )}
                      errorText={get(
                        formState?.errors,
                        `auditEndDateString.message`
                      )}
                    >
                      <ControlledDatePicker
                        name="auditEndDateString"
                        control={control}
                        placeholder="YYYY/MM/DD"
                        className={styles.dateCustomPicker}
                      />
                    </FormField>
                  </ColumnLayout>

                  <Header variant="h3">Planning</Header>
                  <ColumnLayout columns={2}>
                    <FormField
                      label={createLabel(
                        "Audit initiation date",
                        "auditInitiationDateString"
                      )}
                      description="Audit notice date"
                      errorText={get(
                        formState?.errors,
                        `auditInitiationDateString.message`
                      )}
                    >
                      <ControlledDatePicker
                        name="auditInitiationDateString"
                        control={control}
                        placeholder="YYYY/MM/DD"
                        className={styles.dateCustomPicker}
                      />
                    </FormField>
                    <FormField
                      label={createLabel(
                        "Statute of limitations",
                        "statuteOfLimitationsString"
                      )}
                      errorText={get(
                        formState?.errors,
                        `statuteOfLimitationsString.message`
                      )}
                    >
                      <ControlledDatePicker
                        name="statuteOfLimitationsString"
                        control={control}
                        placeholder="YYYY/MM/DD"
                        className={styles.dateCustomPicker}
                      />
                    </FormField>
                    <FormField
                      label={createLabel(
                        "Audit close date",
                        "auditCloseDateString"
                      )}
                      errorText={get(
                        formState?.errors,
                        `auditCloseDateString.message`
                      )}
                    >
                      <ControlledDatePicker
                        name="auditCloseDateString"
                        control={control}
                        placeholder="YYYY/MM/DD"
                        className={styles.dateCustomPicker}
                      />
                    </FormField>
                    <FormField
                      label={createLabel("Audit owner", "owner")}
                      errorText={get(formState?.errors, `owner.message`)}
                    >
                      <ControlledSelect
                        name="owner"
                        control={control}
                        options={data.ownerOptions}
                        filteringType="auto"
                      />
                    </FormField>
                  </ColumnLayout>

                  {auditCustomAttributes &&
                    auditCustomAttributes.length > 0 && (
                      <>
                        <Header variant="h3">Additional information</Header>
                        <ColumnLayout columns={2}>
                          {auditCustomAttributes.map(
                            (attributeSchema: {
                              attributeName: React.Key | null | undefined;
                            }) => (
                              <DynamicFormField
                                key={attributeSchema.attributeName}
                                attributeSchema={attributeSchema}
                                formState={formState}
                                control={control}
                                namePrefix={CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}
                                mode={mode}
                              />
                            )
                          )}
                        </ColumnLayout>
                      </>
                    )}

                  <ColumnLayout columns={1}>
                    <FormField
                      label={createLabel("Description", "description")}
                      errorText={get(formState?.errors, `description.message`)}
                    >
                      <ControlledTextarea
                        name="description"
                        control={control}
                      />
                    </FormField>
                  </ColumnLayout>
                </SpaceBetween>
              </Container>
              {wizardMode && (
                <Container
                  header={
                    <Header>
                      <Header variant="h2">Templates</Header>
                    </Header>
                  }
                >
                  <FormField
                    label={createLabel("Template selection", "templateId")}
                    description={
                      <>
                        Task groups and tasks will be created according to the
                        template. See{" "}
                        <Link href={`/team/?tabId=templates`}>
                          Team Settings
                        </Link>{" "}
                        for detailed information.
                      </>
                    }
                  >
                    <ControlledSelect
                      name="templateId"
                      control={control}
                      filteringType="auto"
                      placeholder="Choose a template"
                      options={[
                        ...((!getTemplatesByTeamName.has(teamName)
                          ? [
                              {
                                value: undefined,
                                label:
                                  "There are currently no templates defined for this team",
                              },
                            ]
                          : getTemplatesByTeamName.get(teamName)) || []),
                      ]}
                    />
                  </FormField>
                </Container>
              )}
            </SpaceBetween>
            {!wizardMode && (
              <>
                <br />
                <Container
                  header={
                    <Header>
                      <Header variant="h2">Contact</Header>
                    </Header>
                  }
                >
                  <FormField
                    label={createLabel("Select contact", "contactIds")}
                    description="You can select up to 10 contacts"
                    errorText={get(formState?.errors, `contactIds.message`)}
                  >
                    <ControlledMultiselect
                      name={"contactIds"}
                      control={control}
                      placeholder="Search by name or email"
                      filteringType="auto"
                      options={
                        contactsList?.contacts?.contacts.map((contact) => ({
                          value: contact.contactId,
                          label: contact.fullName,
                          description: contact.email,
                        })) ?? []
                      }
                    ></ControlledMultiselect>
                  </FormField>
                </Container>
                <br></br>
                <Container
                  header={
                    <Header>
                      <Header variant="h2">Templates</Header>
                    </Header>
                  }
                >
                  <FormField
                    label={createLabel("Template selection", "templateId")}
                    description={
                      <>
                        Task groups and tasks will be created according to the
                        template. See{" "}
                        <Link href={`/team/?tabId=templates`}>
                          Team Settings
                        </Link>{" "}
                        for detailed information.
                      </>
                    }
                  >
                    <ControlledSelect
                      name="templateId"
                      control={control}
                      filteringType="auto"
                      placeholder="Choose a template"
                      options={
                        !getTemplatesByTeamName.has(teamName)
                          ? [
                              {
                                value: undefined,
                                label:
                                  "There are currently no templates defined for this team",
                              },
                            ]
                          : getTemplatesByTeamName.get(teamName)
                      }
                    ></ControlledSelect>
                  </FormField>
                </Container>
                <br></br>
                <Container
                  header={
                    <Header>
                      <Header variant="h2">
                        Upload audit notice -
                        <Box
                          variant="awsui-value-large"
                          fontWeight="bold"
                          fontSize="heading-l"
                        >
                          <i> optional</i>
                        </Box>
                      </Header>
                    </Header>
                  }
                >
                  <br />
                  <FormField>
                    <div>
                      <UploadFile
                        setFiles={setFiles}
                        files={files}
                      ></UploadFile>
                    </div>
                  </FormField>
                </Container>
              </>
            )}
          </Form>
        </form>
      </div>
    );
  }
);

export default CreateAuditForm;
